/**
 * StatRow — a label on the left, a value on the right, that NEVER overflows.
 *
 * The structural fix for card overflow: the label takes the remaining space
 * (`flex: 1, minWidth: 0`) and truncates with an ellipsis; the value never
 * shrinks (`flexShrink: 0`) and never wraps. Use this anywhere a card shows
 * "Label ............ value" rows (Catalog totals, stat lists, etc).
 */
import React from 'react';
import { View, Text, ViewStyle, TextStyle } from 'react-native';
import { useTheme } from '../context/themeContext';

interface StatRowProps {
  label: string;
  value: React.ReactNode;
  /** Use the monospace font for the value (numbers). Default true. */
  mono?: boolean;
  labelStyle?: TextStyle;
  valueStyle?: TextStyle;
  style?: ViewStyle;
}

export function StatRow({ label, value, mono = true, labelStyle, valueStyle, style }: StatRowProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;

  return (
    <View style={[{ flexDirection: 'row', alignItems: 'center', gap: 8 }, style]}>
      <Text
        numberOfLines={1}
        style={[
          { flex: 1, minWidth: 0, fontSize: fontSize.sm, color: colors.ink2, fontFamily: theme.font.sans },
          labelStyle,
        ]}
      >
        {label}
      </Text>
      <Text
        numberOfLines={1}
        style={[
          {
            flexShrink: 0,
            fontSize: fontSize.md,
            fontWeight: fontWeight.semibold,
            color: colors.ink,
            fontFamily: mono ? theme.font.mono : theme.font.sans,
          },
          valueStyle,
        ]}
      >
        {value}
      </Text>
    </View>
  );
}

export default StatRow;
